// The cart, in the drawer and on its own page. Every line is priced again by the server for the Minecraft account being
// shopped on, so what is shown here is always what the server currently thinks; checkout checks it once more before it charges.
import { useState } from "react";
import { Link } from "react-router-dom";
import { DiscordMark, Icon } from "../../icons";
import { EmptyState, Skeleton, errorMessage } from "../../ui";
import { useConfirm } from "../../ui/admin";
import { signInHref } from "./HeaderStore";
import { Tile } from "./ItemCard";
import { PriceTag, QuantityStepper } from "./Price";
import { useEnsureAccounts, useStore } from "./StoreSession";
import { CURRENCY_NAME, platformLabel, type CartLine, type Currency } from "./storeSite";

const key = (line: Pick<CartLine, "itemId" | "currency">) => `${line.itemId}:${line.currency}`;

export function CartView({ returnPath }: { returnPath: string }) {
  const store = useStore();
  useEnsureAccounts();
  const confirm = useConfirm();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState("");
  const cart = store.cart;

  const run = async (token: string, work: () => Promise<void>) => {
    setBusy(token); setError("");
    try { await work(); } catch (reason) { setError(errorMessage(reason, "Your cart could not be updated. Try again.")); }
    finally { setBusy(null); }
  };
  const clear = async () => {
    const answer = await confirm({ title: "Empty your cart?", impact: ["Every item is removed from your cart."], confirmLabel: "Empty cart", tone: "danger" });
    if (answer) await run("clear", store.clear);
  };

  if (!store.signedIn) return <EmptyState icon="lock" title="Sign in to see your cart" description="Your cart is saved to your Discord account, so it follows you between devices."
    action={<a className="button discord" href={signInHref(returnPath)}><DiscordMark/>Sign in with Discord</a>}/>;
  if (!cart) return <Skeleton rows={3} label="Loading your cart"/>;
  if (cart.lines.length === 0) return <EmptyState icon="cart" title="Your cart is empty" description="Add ranks, keys, and perks from the store and they will wait for you here."
    action={<Link className="button primary" to="/store">Browse the store</Link>}/>;

  const totals = (Object.keys(cart.totals) as Currency[]).filter((currency) => cart.lines.some((line) => line.currency === currency));
  return <div className="cart">
    {error && <p className="field-error" role="alert">{error}</p>}
    <ul className="cart-lines">{cart.lines.map((line) => {
      const token = key(line);
      const blocked = !line.available || line.redundant;
      return <li key={token} className={`cart-line ${blocked ? "is-blocked" : ""}`}>
        <Tile icon={line.icon} type={line.type}/>
        <div className="cart-line-main">
          <h3>{line.slug ? <Link to={{ pathname: "/store", search: `?item=${encodeURIComponent(line.slug)}` }}>{line.name}</Link> : line.name}</h3>
          {line.slug && (line.quantity > 1 || line.saleActive || line.upgradeFrom) && <p className="cart-line-price">
            {line.quantity > 1 && <>{line.quantity} × </>}<PriceTag currency={line.currency} amount={line.unitPrice} was={line.saleActive ? line.listPrice : null}/>
            {line.upgradeFrom && <small>Upgrade from {line.upgradeFrom.name}</small>}
          </p>}
          {line.reasonText && blocked && <p className="store-blocked">{line.reasonText}</p>}
        </div>
        <div className="cart-line-side">
          {line.slug && (line.redundant ? <span className="cart-included">Included</span> : line.available && <span className="cart-line-total"><span className="sr-only">Line total </span><PriceTag currency={line.currency} amount={line.total}/></span>)}
          {line.maxQuantity > 1 && <QuantityStepper value={line.quantity} max={line.maxQuantity} label={`Quantity of ${line.name}`} disabled={busy !== null} onChange={(value) => run(token, () => store.setQuantity(line.itemId, line.currency, value))}/>}
          <button type="button" className="button ghost compact" aria-label={`Remove ${line.name} from your cart`} disabled={busy !== null} onClick={() => run(token, () => store.remove(line.itemId, line.currency, line.name))}><Icon name="trash" size={16}/>Remove</button>
        </div>
      </li>;
    })}</ul>

    {totals.length > 0 && <dl className="cart-totals">{totals.map((currency) => <div key={currency}><dt>{CURRENCY_NAME[currency]}</dt><dd>{new Intl.NumberFormat().format(cart.totals[currency])}</dd></div>)}</dl>}

    {cart.account
      ? <p className="fine-print store-priced-for">Priced for <strong>{cart.account.name}</strong> ({platformLabel(cart.account)}).{(store.accounts?.length ?? 0) > 1 && <> <button type="button" className="text-link" aria-haspopup="dialog" onClick={store.openAccount}>Change account</button></>}</p>
      : <p className="fine-print">Link a Minecraft account to buy. <button type="button" className="text-link" aria-haspopup="dialog" onClick={store.openAccount}>Link an account</button></p>}
    {!cart.ok && <p className="store-blocked">Some items cannot be bought right now. Remove them, or fix what is shown under them, to check out.</p>}
    <p className="fine-print">{cart.note}</p>

    <div className="cart-actions">
      {cart.ok && cart.account
        ? <Link className="button primary block" to="/store/checkout">Checkout</Link>
        : <><button type="button" className="button primary block" disabled aria-describedby="checkout-why">Checkout</button>
          <p id="checkout-why" className="fine-print">{cart.account ? "Sort out the items above first." : "Link a Minecraft account to check out."}</p></>}
      <button type="button" className="button ghost compact" disabled={busy !== null} onClick={clear}>Clear cart</button>
    </div>
  </div>;
}
